/**
 * Servicio de métricas — lee desde Realtime Database, nodo `/metrics`.
 *
 * Estructura genérica esperada en Firebase RTDB:
 *
 *   /metrics/
 *     $metricId/
 *       value:     string  (requerido — el número o cifra principal)
 *       label:     string  (requerido — texto descriptivo)
 *       prefix:    string  (opcional — se renderea pequeño antes del valor, p.ej. "+", ">")
 *       suffix:    string  (opcional — se renderea pequeño después, p.ej. "%", "días")
 *       labelNote: string  (opcional — texto secundario gris)
 *       order:     number  (requerido)
 *       active:    boolean (requerido)
 *
 * Reglas en `database.rules.json`: `/metrics` con `.read: true` y `.write: false`.
 */
import { ref, onValue, query, orderByChild } from 'firebase/database';

import type { Metric, MetricSnapshot } from '../types/domain';

import { db } from './firebaseConfig';
import type { Unsubscribe } from './teamService';

export type MetricsSubscriptionCallback = (metrics: Metric[], error: Error | null) => void;

export function normalizeMetricsSnapshot(value: unknown): Metric[] {
  if (!value || typeof value !== 'object') return [];

  const record = value as Record<string, MetricSnapshot>;

  const entries = Object.entries(record).map(([id, raw]) => ({
    id,
    value: raw?.value != null ? String(raw.value) : '',
    label: raw?.label ?? '',
    prefix: raw?.prefix ?? '',
    suffix: raw?.suffix ?? '',
    labelNote: raw?.labelNote ?? '',
    order: typeof raw?.order === 'number' ? raw.order : Number.POSITIVE_INFINITY,
    active: raw?.active !== false,
  }));

  return entries
    .filter((m) => m.active && m.value && m.label)
    .sort((a, b) => a.order - b.order);
}

/**
 * Se suscribe al nodo `/metrics` y notifica al callback ante cualquier cambio.
 */
export function subscribeToMetrics(cb: MetricsSubscriptionCallback): Unsubscribe {
  const metricsRef = query(ref(db, '/metrics'), orderByChild('order'));
  const unsubscribe = onValue(
    metricsRef,
    (snapshot) => {
      try {
        const metrics = normalizeMetricsSnapshot(snapshot.val());
        cb(metrics, null);
      } catch (err) {
        cb([], err as Error);
      }
    },
    (err) => cb([], err)
  );
  return unsubscribe;
}
